/**
 * The delivery record: who a report went to, what happened, and when.
 *
 * Shared by the Deliveries tab (every schedule) and a report's own page (its
 * schedules only), so the two can never describe the same send differently.
 */
import { Download } from 'lucide-react'
import clsx from 'clsx'
import {
  DELIVERY_FILTERS,
  matchesDeliveryFilter,
  receiptsNote,
  summarizeDeliveries,
  type DeliveryFilter,
  type DeliveryRow,
} from './deliveries'
import { formatWhen, plural } from './format'
import type { ReportDeliveryHistory } from '@/pages/pipeline-intelligence/types'
import { Btn, Chip, EmptyState, ErrorBanner, Pill, SkeletonRows, Tile } from './ui'
import type { ReportActions } from './useReportActions'
import type { ReportPermissions } from './useReportingData'

export function DeliveryTable({
  rows,
  history,
  loading,
  error,
  onRetry,
  filter,
  onFilter,
  now,
  actions,
  perms,
  emptyTitle,
  emptyBody,
  label,
}: {
  rows: DeliveryRow[]
  history: ReportDeliveryHistory | undefined
  loading: boolean
  error: boolean
  onRetry: () => void
  filter: DeliveryFilter
  onFilter: (filter: DeliveryFilter) => void
  now: Date
  actions: ReportActions
  perms: ReportPermissions
  emptyTitle: string
  emptyBody: string
  label: string
}) {
  const summary = summarizeDeliveries(rows)
  const visible = rows.filter((row) => matchesDeliveryFilter(row, filter))
  const note = receiptsNote(history)

  return (
    <section aria-label={label} className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter deliveries">
        {DELIVERY_FILTERS.map((entry) => (
          <Chip key={entry.id} active={filter === entry.id} onClick={() => onFilter(entry.id)}>
            {entry.label}
          </Chip>
        ))}
      </div>

      {error ? (
        <ErrorBanner onRetry={onRetry}>The delivery ledger could not be read, so nothing is listed.</ErrorBanner>
      ) : null}

      {loading ? (
        <SkeletonRows label="Loading deliveries" count={3} />
      ) : error ? null : rows.length === 0 ? (
        <EmptyState title={emptyTitle} body={emptyBody} />
      ) : (
        <>
          <div className="flex flex-wrap gap-2.5" aria-label="Delivery summary">
            <Tile label="Total" value={summary.total} />
            <Tile label="Sent" value={summary.sent} />
            <Tile label="Delivered" value={summary.delivered} />
            <Tile label="Opened" value={summary.opened} />
            <Tile label="Failed" value={summary.failed} tone={summary.failed ? 'bad' : undefined} />
            <Tile label="Sending" value={summary.sending} />
            <Tile label="Kept, not emailed" value={summary.kept} />
          </div>

          {visible.length === 0 ? (
            <EmptyState title="Nothing matches this filter" body="Choose All to see every delivery." />
          ) : (
            <div className="overflow-x-auto rounded-[10px] border border-[#1E1E1E] bg-[#0B0B0B]">
              <table className="w-full min-w-[980px] border-collapse text-[12.5px]">
                <caption className="sr-only">{label}</caption>
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-[.12em] text-[#8C8C8C]">
                    <th scope="col" className="w-[190px] px-3 py-2.5 font-medium">Recipient</th>
                    <th scope="col" className="w-[200px] px-3 py-2.5 font-medium">Report</th>
                    <th scope="col" className="w-[150px] px-3 py-2.5 font-medium">Status</th>
                    <th scope="col" className="w-[130px] px-3 py-2.5 font-medium">When</th>
                    <th scope="col" className="px-3 py-2.5 font-medium">What happened</th>
                    <th scope="col" className="w-[90px] px-3 py-2.5 font-medium">File</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr key={row.id} className="border-t border-[#151515] align-top">
                      <td className={clsx('px-3 py-2.5 [overflow-wrap:anywhere]', row.noEmail ? 'text-[#8F8F8F]' : 'text-[#ECEAE4]')}>
                        {row.who}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-[#ECEAE4]">{row.reportTitle}</div>
                        {row.scheduleName !== row.reportTitle ? (
                          <div className={clsx('text-[11.5px]', row.scheduleDeleted ? 'text-[#F0B08A]' : 'text-[#8F8F8F]')}>
                            {row.scheduleName}
                          </div>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <Pill tone={row.tone}>{row.statusLabel}</Pill>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-[#B9B9B9]">{formatWhen(row.at, { now })}</td>
                      <td className="max-w-[360px] px-3 py-2.5 text-[#B9B9B9]">{row.detail}</td>
                      <td className="px-3 py-2.5">
                        {row.downloadable && row.reportId ? (
                          <Btn
                            size="sm"
                            variant="quiet"
                            icon={<Download className="h-3.5 w-3.5" aria-hidden />}
                            disabled={!perms.canRun || actions.downloadStored.isPending}
                            title={perms.canRun ? undefined : 'Your role cannot take report files.'}
                            aria-label={`Download the ${row.format === 'xlsx' ? 'Excel' : 'PDF'} file for ${row.reportTitle}`}
                            onClick={() =>
                              actions.downloadStored.mutate({
                                run: { id: row.reportId as string, format: row.format },
                                name: row.reportTitle,
                              })
                            }
                          >
                            {row.format === 'xlsx' ? 'Excel' : 'PDF'}
                          </Btn>
                        ) : (
                          <span className="text-[#808080]">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[12px] text-[#8C8C8C]">
            {plural(visible.length, 'delivery', 'deliveries')} shown.{note ? ` ${note}` : ''}
          </p>
        </>
      )}
    </section>
  )
}
